import { useCallback, useContext, useEffect, useState } from 'react';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { BeatLoader } from 'react-spinners';
import { PlusIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { FiAlertCircle } from 'react-icons/fi';
import { IColumn, Table } from '../../components/tailwind/table';
import {
  DEFAULT_DETAIL_OPEN,
  DEFAULT_ERROR_MESSAGE,
  DEFAULT_LOADING_MESSAGE,
  DEFAULT_SELECTED_TAB,
  DEFAULT_TOAST_DURATION,
  DEFAULT_TOAST_POSITION,
  classNames,
} from '../../utils';
import { Modal } from '../../components/tailwind/modal';
import DetailForm, { DetailSchema } from '../../components/detailForm';
import { ISorting } from '../../types';
import { SearchContext } from '../../context/searchContext';
import { CREATE_CUSTOMER, GET_CUSTOMER, GET_CUSTOMERS, GET_CUSTOMER_DEBOUNCE, ICustomer, UPDATE_CUSTOMER } from '../../utils/customer';
import Button from '../../components/tailwind/button';
import { toastError, toastSuccess } from '../../utils/toast';
import useDebounce from '../../components/tailwind/hooks/useDebounce';
import { GET_BILLINGCUSTOMER_DEBOUNCE } from '../../utils/billingCustomer';

export const Customers: React.FC = ({ ...props }) => {
  // ===========================================
  // ============== GENERAL STATE ==============

  const { searchQuery, setSearchQuery } = useContext(SearchContext);
  useEffect(() => {
    setSearchQuery('');
  }, []);

  const [pagedState, setPagedState] = useState({
    skip: 0,
    take: 10,
  });

  // ===========================================
  // =============== CUSTOMER STATE ============

  const [selectedCustomer, setSelectedCustomer] = useState<ICustomer | undefined>();
  const [selectedCustomerTab, setSelectedCustomerTab] = useState(DEFAULT_SELECTED_TAB);
  const [sortingCustomers, setSortingCustomers] = useState<{ key: string; direction: string }>({
    key: 'createdAt',
    direction: 'desc',
  });
  const [openCustomerDetail, setOpenCustomerDetail] = useState(DEFAULT_DETAIL_OPEN);
  const [openCustomerCreate, setOpenCustomerCreate] = useState(DEFAULT_DETAIL_OPEN);

  const [institutionID, setInstitutionID] = useState('');
  const debouncedInstitutionIDQuery = useDebounce(institutionID, 500);

  const [billingID, setBillingID] = useState('');
  const debouncedBillingIDQuery = useDebounce(billingID, 500);

  useEffect(() => {
    setPagedState({
      skip: 0,
      take: 10,
    });
  }, [searchQuery]);

  useEffect(() => {
    getDebounceCustomer({
      variables: {
        institutionID: debouncedInstitutionIDQuery,
      },
      onCompleted: data => {
        if (data.findOneCustomerByInstitutionID && data.findOneCustomerByInstitutionID !== null) {
          setSelectedCustomer((prevState: any) => ({
            ...prevState,
            institution: data.findOneCustomerByInstitutionID?.institution,
            phone: data.findOneCustomerByInstitutionID?.phone,
            email: data.findOneCustomerByInstitutionID?.email,
            address: {
              address: data.findOneCustomerByInstitutionID?.address?.address,
              zipcode: data.findOneCustomerByInstitutionID?.address?.zipcode,
              city: data.findOneCustomerByInstitutionID?.address?.city,
              country: data.findOneCustomerByInstitutionID?.address?.country,
            },
            hasBillingCustomer: data.findOneCustomerByInstitutionID?.billingCustomer ? true : false,
            billingCustomer: data.findOneCustomerByInstitutionID?.billingCustomer
              ? data.findOneCustomerByInstitutionID?.billingCustomer
              : undefined,
          }));
        }
      },
    });
  }, [debouncedInstitutionIDQuery]);

  const customerColumns: IColumn[] = [
    {
      key: 'column1',
      name: 'Klant',
      fieldName: 'customer.institution',
      render: (item: ICustomer) => {
        return <span className='text-ellipsis text-base overflow-hidden block w-18'>{item?.institution}</span>;
      },
    },
    {
      key: 'column2',
      name: 'Email',
      fieldName: 'event.activity.title',
      render: (item: ICustomer) => {
        return <span className='text-ellipsis text-base overflow-hidden block w-46'>{item?.email}</span>;
      },
    },
    {
      key: 'column3',
      name: 'Plaats',
      fieldName: 'event.location.name',
      render: (item: ICustomer) => {
        return <span className='text-ellipsis overflow-hidden block w-18 text-base'>{item?.address.city}</span>;
      },
    },
  ];

  const getOrderBy = useCallback(
    (sortingOrders: any) => {
      return { [sortingOrders.key]: sortingOrders.direction === 'asc' ? 'asc' : 'desc' };
    },
    [sortingCustomers],
  );

  const getFilters = (filterString: string) => {
    const filterArray = filterString.split(' ');

    const filterObject: any = {
      AND: [],
    };

    for (let i = 0; i < filterArray.length; i++) {
      const filterValue: any = {
        OR: [{ institution: { contains: filterArray[i] } }],
      };

      filterObject.AND.push(filterValue);
    }

    return filterObject;
  };

  const {
    data: dataCustomers,
    loading: loadingCustomers,
    error: errorCustomers,
    refetch: refetchCustomers,
    fetchMore: fetchMoreCustomers,
  } = useQuery(GET_CUSTOMERS, {
    variables: {
      filter: getFilters(searchQuery),
      orderBy: getOrderBy(sortingCustomers),
      skip: pagedState.skip,
      take: pagedState.take,
    },
  });

  const [getCustomer, { loading: loadingCustomer, error: errorCustomer, refetch: refetchCustomer }] = useLazyQuery(GET_CUSTOMER);
  const [getDebounceCustomer, { loading: loadingDebounceCustomer, error: errorDebounceCustomer, refetch: refetchDebounceCustomer }] =
    useLazyQuery(GET_CUSTOMER_DEBOUNCE);
  const [createCustomer] = useMutation(CREATE_CUSTOMER);
  const [updateCustomer] = useMutation(UPDATE_CUSTOMER);

  const handleSetCustomerSelection = (customer?: ICustomer) => {
    if (customer?.id) {
      getCustomer({
        variables: {
          where: {
            id: customer?.id,
          },
        },
        onCompleted: data => {
          setSelectedCustomer({ ...data.findOneCustomer, hasBillingCustomer: data.findOneCustomer.billingCustomer ? true : false });
          setOpenCustomerDetail(true);
        },
      });
    } else {
      setSelectedCustomer(undefined);
    }

    setOpenCustomerDetail(true);
  };

  const handleUpdateCustomer = async () => {
    try {
      const res = await toast.promise(
        new Promise((resolve, reject) => {
          if (selectedCustomer?.id) {
            const updateCustomerObj: any = {
              billingID: selectedCustomer?.billingID,
              glnID: selectedCustomer?.glnID,
              institution: selectedCustomer?.institution,
              institutionID: selectedCustomer?.institutionID,
              address: {
                address: selectedCustomer?.address?.address,
                zipcode: selectedCustomer?.address?.zipcode,
                city: selectedCustomer?.address?.city,
                country: selectedCustomer?.address?.country,
              },
              email: selectedCustomer?.email,
              phone: selectedCustomer?.phone,
              billingCustomer: (selectedCustomer as any).hasBillingCustomer
                ? {
                    billingID: selectedCustomer?.billingCustomer?.billingID,
                    glnID: selectedCustomer?.billingCustomer?.glnID,
                    institution: selectedCustomer?.billingCustomer?.institution,
                    address: {
                      address: selectedCustomer?.billingCustomer?.address?.address,
                      zipcode: selectedCustomer?.billingCustomer?.address?.zipcode,
                      city: selectedCustomer?.billingCustomer?.address?.city,
                      country: selectedCustomer?.billingCustomer?.address?.country,
                    },
                    email: selectedCustomer?.billingCustomer?.email,
                    phone: selectedCustomer?.billingCustomer?.phone,
                  }
                : undefined,
              hasBillingCustomer: (selectedCustomer as any).hasBillingCustomer ? true : false,
            };

            console.log('updateCustomerObj', updateCustomerObj);

            updateCustomer({
              variables: {
                id: selectedCustomer?.id,
                data: updateCustomerObj,
              },
              onCompleted: data => {
                resolve({ ...data.updateCustomer, hasBillingCustomer: data.updateCustomer.billingCustomer ? true : false });
              },
              onError: error => {
                reject(error);
              },
            });
          }
        }),
        {
          pending: {
            position: DEFAULT_TOAST_POSITION,
            render() {
              return DEFAULT_LOADING_MESSAGE;
            },
          },
          error: {
            position: DEFAULT_TOAST_POSITION,
            render() {
              return DEFAULT_ERROR_MESSAGE;
            },
            icon: FiAlertCircle,
          },
        },
        {
          autoClose: DEFAULT_TOAST_DURATION,
        },
      );

      const newUpdatedCustomer: ICustomer = (await res) as any;

      setSelectedCustomer(newUpdatedCustomer);
      refetchCustomers({
        filter: getFilters(searchQuery),
        orderBy: getOrderBy(sortingCustomers),
        skip: 0,
        take: pagedState.skip > 0 ? pagedState.skip : pagedState.take,
      });

      toastSuccess('Klant succesvol geupdate');
    } catch (error) {
      const message =
        error && (error as any).graphQLErrors && (error as any).graphQLErrors.length > 0 && (error as any).graphQLErrors[0].message
          ? (error as any).graphQLErrors[0].message
          : (error as any).message
          ? (error as any).message
          : DEFAULT_ERROR_MESSAGE;
      toastError(message);
    }
  };

  const handleCreateCustomer = async () => {
    try {
      const res = await toast.promise(
        new Promise((resolve, reject) => {
          if (!selectedCustomer?.id) {
            const createCustomerObj: any = {
              billingID: selectedCustomer?.billingID,
              glnID: selectedCustomer?.glnID,
              institution: selectedCustomer?.institution,
              institutionID: selectedCustomer?.institutionID,
              address: {
                address: selectedCustomer?.address?.address,
                zipcode: selectedCustomer?.address?.zipcode,
                city: selectedCustomer?.address?.city,
                country: selectedCustomer?.address?.country,
              },
              email: selectedCustomer?.email,
              phone: selectedCustomer?.phone,
              billingCustomer: (selectedCustomer as any).hasBillingCustomer
                ? {
                    billingID: selectedCustomer?.billingCustomer?.billingID,
                    glnID: selectedCustomer?.billingCustomer?.glnID,
                    institution: selectedCustomer?.billingCustomer?.institution,
                    address: {
                      address: selectedCustomer?.billingCustomer?.address?.address,
                      zipcode: selectedCustomer?.billingCustomer?.address?.zipcode,
                      city: selectedCustomer?.billingCustomer?.address?.city,
                      country: selectedCustomer?.billingCustomer?.address?.country,
                    },
                    email: selectedCustomer?.billingCustomer?.email,
                    phone: selectedCustomer?.billingCustomer?.phone,
                  }
                : undefined,
              hasBillingCustomer: (selectedCustomer as any).hasBillingCustomer ? true : false,
            };

            createCustomer({
              variables: {
                data: createCustomerObj,
              },
              onCompleted: data => {
                resolve({ ...data.createCustomer, hasBillingCustomer: data.createCustomer.billingCustomer ? true : false });
              },
              onError: error => {
                reject(error);
              },
            });
          }
        }),
        {
          pending: {
            position: DEFAULT_TOAST_POSITION,
            render() {
              return DEFAULT_LOADING_MESSAGE;
            },
          },
          error: {
            position: DEFAULT_TOAST_POSITION,
            render() {
              return DEFAULT_ERROR_MESSAGE;
            },
            icon: FiAlertCircle,
          },
        },
        {
          autoClose: DEFAULT_TOAST_DURATION,
        },
      );

      const customer: ICustomer = (await res) as any;
      setSelectedCustomer(customer);
      refetchCustomers({
        filter: getFilters(searchQuery),
        orderBy: getOrderBy(sortingCustomers),
        skip: 0,
        take: pagedState.skip > 0 ? pagedState.skip : pagedState.take,
      });

      toastSuccess('Klant succesvol aangemaakt');
    } catch (error) {
      const message =
        error && (error as any).graphQLErrors && (error as any).graphQLErrors.length > 0 && (error as any).graphQLErrors[0].message
          ? (error as any).graphQLErrors[0].message
          : (error as any).message
          ? (error as any).message
          : DEFAULT_ERROR_MESSAGE;
      toastError(message);
    }
  };

  const customerDetailSchema: DetailSchema = {
    id: 'order_detail',
    object: selectedCustomer,
    permission: '',
    selectedTab: selectedCustomerTab,
    setSelectedTab: setSelectedCustomerTab,
    handleUpdate: handleUpdateCustomer,
    handleCreate: handleCreateCustomer,
    delete: {
      title: 'Bestelling verwijderen?',
      text: 'Weet je zeker dat je deze bestelling wilt verwijderen? Dit kan niet ongedaan worden gemaakt.',
    },
    sections: [
      {
        id: 'customer_detail_section_x',
        title: 'Algemeen',
        width: 100,
        fields: [
          {
            id: 'customer_detail_section_0_field_0',
            label: 'Naam',
            name: 'institution',
            required: true,
            value: selectedCustomer?.institution,
            placeholder: '',
            type: 'text',
            width: 48.5,
            callback: (name: string, value: string) => {
              setSelectedCustomer((prevState: any) => ({
                ...prevState,
                [name]: value,
              }));
              return value;
            },
            validateCallback: (value?: string): { valid: boolean; message?: string } => {
              if (!value) {
                return {
                  valid: false,
                  message: 'Naam moet worden ingevuld',
                };
              }

              return { valid: true };
            },
            disableCallback: () => {
              return selectedCustomer?.id === 34 ? true : false;
            },
          },
          {
            id: 'customer_detail_section_0_field_1',
            label: 'Instellingsnummer',
            name: 'institutionID',
            value: selectedCustomer?.institutionID,
            placeholder: '',
            type: 'text',
            width: 48.5,
            callback: (name: string, value: string) => {
              setSelectedCustomer((prevState: any) => ({
                ...prevState,
                [name]: value,
              }));

              setInstitutionID(value);
              return value;
            },
            disableCallback: () => {
              return selectedCustomer?.id === 34 ? true : false;
            },
          },
          {
            id: 'customer_detail_section_0_field_8',
            label: 'BTW / ondernemingsnummer',
            name: 'billingID',
            value: selectedCustomer?.billingID,
            placeholder: '',
            type: 'text',
            width: 48.5,
            callback: (name: string, value: string) => {
              setSelectedCustomer((prevState: any) => ({
                ...prevState,
                [name]: value,
              }));
              return value;
            },
            disableCallback: () => {
              return selectedCustomer?.id === 34 ? true : false;
            },
          },
          {
            id: 'customer_detail_section_0_field_887877',
            label: 'Gln nummer',
            name: 'glnID',
            value: selectedCustomer?.glnID,
            placeholder: '',
            type: 'text',
            width: 48.5,
            callback: (name: string, value: string) => {
              setSelectedCustomer((prevState: any) => ({
                ...prevState,
                glnID: value,
              }));

              console.log('selectedCustomer', selectedCustomer);
              return value;
            },
            disableCallback: () => {
              return selectedCustomer?.id === 34 ? true : false;
            },
          },
          {
            id: 'customer_detail_section_0_field_3',
            label: 'Telefoonnummer',
            name: 'phone',
            required: true,
            value: selectedCustomer?.phone,
            placeholder: '',
            type: 'text',
            width: 48.5,
            callback: (name: string, value: string) => {
              setSelectedCustomer((prevState: any) => ({
                ...prevState,
                [name]: value,
              }));
              return value;
            },
            validateCallback: (value?: string): { valid: boolean; message?: string } => {
              if (!value) {
                return {
                  valid: false,
                  message: 'Telefoon moet worden ingevuld',
                };
              }

              return { valid: true };
            },
            disableCallback: () => {
              return selectedCustomer?.id === 34 ? true : false;
            },
          },
          {
            id: 'customer_detail_section_0_field_2',
            label: 'Email',
            name: 'email',
            required: true,
            value: selectedCustomer?.email,
            placeholder: '',
            type: 'text',
            width: 48.5,
            callback: (name: string, value: string) => {
              setSelectedCustomer((prevState: any) => ({
                ...prevState,
                [name]: value,
              }));
              return value;
            },
            disableCallback: () => {
              return selectedCustomer?.id === 34 ? true : false;
            },
            validateCallback: (value?: string): { valid: boolean; message?: string } => {
              if (!value) {
                return {
                  valid: false,
                  message: 'Email moet worden ingevuld',
                };
              }

              return { valid: true };
            },
          },
          {
            id: 'customer_detail_section_0_field_4',
            label: 'Address',
            name: 'address',
            required: true,
            value: selectedCustomer?.address?.address,
            placeholder: '',
            type: 'text',
            width: 48.5,
            callback: (name: string, value: string) => {
              setSelectedCustomer((prevState: any) => ({
                ...prevState,
                address: {
                  ...prevState.address,
                  [name]: value,
                },
              }));
              return value;
            },
            disableCallback: () => {
              return selectedCustomer?.id === 34 ? true : false;
            },
            validateCallback: (value?: string): { valid: boolean; message?: string } => {
              if (!value) {
                return {
                  valid: false,
                  message: 'Address moet worden ingevuld',
                };
              }

              return { valid: true };
            },
          },
          {
            id: 'customer_detail_section_0_field_5',
            label: 'Postcode',
            name: 'zipcode',
            required: true,
            value: selectedCustomer?.address?.zipcode,
            placeholder: '',
            type: 'text',
            width: 48.5,
            callback: (name: string, value: string) => {
              setSelectedCustomer((prevState: any) => ({
                ...prevState,
                address: {
                  ...prevState.address,
                  [name]: value,
                },
              }));
              return value;
            },
            disableCallback: () => {
              return selectedCustomer?.id === 34 ? true : false;
            },
            validateCallback: (value?: string): { valid: boolean; message?: string } => {
              if (!value) {
                return {
                  valid: false,
                  message: 'Postcode moet worden ingevuld',
                };
              }

              return { valid: true };
            },
          },
          {
            id: 'customer_detail_section_0_field_6',
            label: 'Plaats',
            name: 'city',
            required: true,
            value: selectedCustomer?.address?.city,
            placeholder: '',
            type: 'text',
            width: 48.5,
            callback: (name: string, value: string) => {
              setSelectedCustomer((prevState: any) => ({
                ...prevState,
                address: {
                  ...prevState.address,
                  [name]: value,
                },
              }));
              return value;
            },
            disableCallback: () => {
              return selectedCustomer?.id === 34 ? true : false;
            },
            validateCallback: (value?: string): { valid: boolean; message?: string } => {
              if (!value) {
                return {
                  valid: false,
                  message: 'Plaats moet worden ingevuld',
                };
              }

              return { valid: true };
            },
          },
          {
            id: 'customer_detail_section_0_field_7',
            label: 'Land',
            name: 'country',
            required: true,
            value: selectedCustomer?.address?.country,
            placeholder: '',
            type: 'text',
            width: 48.5,
            callback: (name: string, value: string) => {
              setSelectedCustomer((prevState: any) => ({
                ...prevState,
                address: {
                  ...prevState.address,
                  [name]: value,
                },
              }));
              return value;
            },
            disableCallback: () => {
              return selectedCustomer?.id === 34 ? true : false;
            },
            validateCallback: (value?: string): { valid: boolean; message?: string } => {
              if (!value) {
                return {
                  valid: false,
                  message: 'Land moet worden ingevuld',
                };
              }

              return { valid: true };
            },
          },
          {
            id: 'customer_detail_section_0_field_9',
            label: 'Factuur klant',
            name: 'billingCustomerID',
            value: (selectedCustomer as any)?.hasBillingCustomer, //custom attr
            type: 'switch',
            width: 48.5,
            callback: (name: string, value: string) => {
              console.log('value', value);
              setSelectedCustomer((prevState: any) => ({
                ...prevState,
                hasBillingCustomer: value,
              }));
              return value;
            },
            disableCallback: () => {
              return selectedCustomer?.id === 34 ? true : true;
            },
            hideCallback: () => {
              if ((selectedCustomer as any)?.id === 34) {
                return false;
              }

              if ((selectedCustomer as any)?.id) {
                return true;
              }

              return false;
            },
          },
        ],
      },
      {
        id: 'customer_detail_section_1',
        title: 'Factuur klant',
        width: 100,
        hideCallback: () => {
          if ((selectedCustomer as any)?.hasBillingCustomer && (selectedCustomer as any)?.id) {
            return true;
          }
          return false;
        },
        fields: [
          {
            id: 'customer_detail_section_1_field_0',
            label: 'Naam',
            name: 'institution',
            required: true,
            value: selectedCustomer?.billingCustomer?.institution,
            placeholder: '',
            type: 'text',
            width: 32.5,
            callback: (name: string, value: string) => {
              setSelectedCustomer((prevState: any) => ({
                ...prevState,
                billingCustomer: {
                  ...prevState.billingCustomer,
                  [name]: value,
                },
              }));
              return value;
            },
            validateCallback: (value?: string): { valid: boolean; message?: string } => {
              if (!value && (selectedCustomer as any)?.hasBillingCustomer) {
                return {
                  valid: false,
                  message: 'Naam moet worden ingevuld',
                };
              }

              return { valid: true };
            },
          },
          {
            id: 'customer_detail_section_1_field_00',
            label: 'BTW / ondernemingsnummer',
            name: 'billingID',
            required: true,
            value: selectedCustomer?.billingCustomer?.billingID,
            placeholder: '',
            type: 'text',
            width: 32.5,
            callback: (name: string, value: string) => {
              setSelectedCustomer((prevState: any) => ({
                ...prevState,
                billingCustomer: {
                  ...prevState.billingCustomer,
                  [name]: value,
                },
              }));
              setBillingID(value);
              return value;
            },
            validateCallback: (value?: string): { valid: boolean; message?: string } => {
              if (!value && (selectedCustomer as any)?.hasBillingCustomer) {
                return {
                  valid: false,
                  message: 'BTW / ondernemingsnummer moet worden ingevuld',
                };
              }

              return { valid: true };
            },
          },
          {
            id: 'customer_detail_section_1_field_008784',
            label: 'Gln nummer',
            name: 'glnID',
            required: true,
            value: selectedCustomer?.billingCustomer?.glnID,
            placeholder: '',
            type: 'text',
            width: 32.5,
            callback: (name: string, value: string) => {
              setSelectedCustomer((prevState: any) => ({
                ...prevState,
                billingCustomer: {
                  ...prevState.billingCustomer,
                  glnID: value,
                },
              }));
              return value;
            },
          },
          {
            id: 'customer_detail_section_0_field_3',
            label: 'Telefoonnummer',
            name: 'phone',
            required: true,
            value: selectedCustomer?.billingCustomer?.phone,
            placeholder: '',
            type: 'text',
            width: 48.5,
            callback: (name: string, value: string) => {
              setSelectedCustomer((prevState: any) => ({
                ...prevState,
                billingCustomer: {
                  ...prevState.billingCustomer,
                  [name]: value,
                },
              }));
              return value;
            },
            validateCallback: (value?: string): { valid: boolean; message?: string } => {
              if (!value && (selectedCustomer as any)?.hasBillingCustomer) {
                return {
                  valid: false,
                  message: 'Telefoon moet worden ingevuld',
                };
              }

              return { valid: true };
            },
          },
          {
            id: 'customer_detail_section_0_field_2',
            label: 'Email',
            name: 'email',
            required: true,
            value: selectedCustomer?.billingCustomer?.email,
            placeholder: '',
            type: 'text',
            width: 48.5,
            callback: (name: string, value: string) => {
              setSelectedCustomer((prevState: any) => ({
                ...prevState,
                billingCustomer: {
                  ...prevState.billingCustomer,
                  [name]: value,
                },
              }));
              return value;
            },
            validateCallback: (value?: string): { valid: boolean; message?: string } => {
              if (!value && (selectedCustomer as any)?.hasBillingCustomer) {
                return {
                  valid: false,
                  message: 'Email moet worden ingevuld',
                };
              }

              return { valid: true };
            },
          },
          {
            id: 'customer_detail_section_0_field_4',
            label: 'Address',
            name: 'address',
            required: true,
            value: selectedCustomer?.billingCustomer?.address?.address,
            placeholder: '',
            type: 'text',
            width: 48.5,
            callback: (name: string, value: string) => {
              setSelectedCustomer((prevState: any) => ({
                ...prevState,
                billingCustomer: {
                  ...prevState.billingCustomer,
                  address: {
                    ...prevState.billingCustomer?.address,
                    [name]: value,
                  },
                },
              }));
              return value;
            },
            validateCallback: (value?: string): { valid: boolean; message?: string } => {
              if (!value && (selectedCustomer as any)?.hasBillingCustomer) {
                return {
                  valid: false,
                  message: 'Address moet worden ingevuld',
                };
              }

              return { valid: true };
            },
          },
          {
            id: 'customer_detail_section_0_field_5',
            label: 'Postcode',
            name: 'zipcode',
            required: true,
            value: selectedCustomer?.billingCustomer?.address?.zipcode,
            placeholder: '',
            type: 'text',
            width: 48.5,
            callback: (name: string, value: string) => {
              setSelectedCustomer((prevState: any) => ({
                ...prevState,
                billingCustomer: {
                  ...prevState.billingCustomer,
                  address: {
                    ...prevState.billingCustomer?.address,
                    [name]: value,
                  },
                },
              }));
              return value;
            },
            validateCallback: (value?: string): { valid: boolean; message?: string } => {
              if (!value && (selectedCustomer as any)?.hasBillingCustomer) {
                return {
                  valid: false,
                  message: 'Postcode moet worden ingevuld',
                };
              }

              return { valid: true };
            },
          },
          {
            id: 'customer_detail_section_0_field_6',
            label: 'Plaats',
            name: 'city',
            required: true,
            value: selectedCustomer?.billingCustomer?.address?.city,
            placeholder: '',
            type: 'text',
            width: 48.5,
            callback: (name: string, value: string) => {
              setSelectedCustomer((prevState: any) => ({
                ...prevState,
                billingCustomer: {
                  ...prevState.billingCustomer,
                  address: {
                    ...prevState.billingCustomer?.address,
                    [name]: value,
                  },
                },
              }));
              return value;
            },
            validateCallback: (value?: string): { valid: boolean; message?: string } => {
              if (!value && (selectedCustomer as any)?.hasBillingCustomer) {
                return {
                  valid: false,
                  message: 'Plaats moet worden ingevuld',
                };
              }

              return { valid: true };
            },
          },
          {
            id: 'customer_detail_section_0_field_7',
            label: 'Land',
            name: 'country',
            required: true,
            value: selectedCustomer?.billingCustomer?.address?.country,
            placeholder: '',
            type: 'text',
            width: 48.5,
            callback: (name: string, value: string) => {
              setSelectedCustomer((prevState: any) => ({
                ...prevState,
                billingCustomer: {
                  ...prevState.billingCustomer,
                  address: {
                    ...prevState.billingCustomer?.address,
                    [name]: value,
                  },
                },
              }));
              return value;
            },
            validateCallback: (value?: string): { valid: boolean; message?: string } => {
              if (!value && (selectedCustomer as any)?.hasBillingCustomer) {
                return {
                  valid: false,
                  message: 'Land moet worden ingevuld',
                };
              }

              return { valid: true };
            },
          },
        ],
      },
    ],
  };

  // ===========================================

  return (
    <div id='customers'>
      <header>
        <h1 className='font-bold text-2xl'>Klanten</h1>
      </header>
      <div>
        <div className='my-4'>
          <Button
            quaternary
            onClick={() => {
              handleSetCustomerSelection();
            }}
          >
            {'Nieuw'}{' '}
            <span className='inline ml-1'>
              <PlusIcon className='h-3 w-3 text-cego-white' aria-hidden='true' />
            </span>
          </Button>
        </div>
        {errorCustomers ? (
          <div className='flex min-h-full flex-col bg-white pt-40 pb-12'>
            <main className='mx-auto flex w-full max-w-7xl flex-grow flex-col justify-center px-6 lg:px-8'>
              <div className='py-16'>
                <div className='text-center'>
                  <p className='text-6xl font-bold text-cego-black'>Error</p>
                  <h1 className='mt-6 text-4xl font-bold tracking-tight text-gray-900 sm:text-base'>Er ging iets mis</h1>
                  <p className='mt-2 text-base text-gray-500'>{'refresh de pagina en probeer opnieuw aan te melden'}</p>
                </div>
              </div>
            </main>
          </div>
        ) : loadingCustomers ? (
          <div>
            <div className={classNames('sweet-loading my-10 text-center block')}>
              <BeatLoader color={'#A9C1C9'} loading={true} size={15} aria-label='Loading Spinner' data-testid='loader' />
            </div>
          </div>
        ) : (
          <div>
            <div>
              <Table
                items={dataCustomers ? dataCustomers.findManyCustomers : []}
                columns={customerColumns}
                loading={loadingCustomers}
                onSelect={handleSetCustomerSelection}
                selection={selectedCustomer}
                lazyLoading={true}
                loadMore={true}
                loadMoreCallback={() => {
                  const skip = pagedState.skip + pagedState.take;
                  fetchMoreCustomers({
                    variables: {
                      filter: getFilters(searchQuery),
                      orderBy: getOrderBy(sortingCustomers),
                      skip: skip,
                      take: pagedState.take,
                    },
                  });

                  setPagedState(prevState => ({
                    ...prevState,
                    skip: skip,
                  }));
                }}
              />
            </div>

            <Modal open={openCustomerDetail} setOpen={setOpenCustomerDetail}>
              <>
                {openCustomerDetail === true ? (
                  <>
                    <div>
                      {selectedCustomer?.id === 34 ? (
                        <div
                          style={{
                            padding: '10px',
                            backgroundColor: 'red',
                            color: 'white',
                          }}
                        >
                          {'Deze klant word gebruikt voor eigen CEGO bestellingen / inscrijvingen.  Contacteer iSmart voor wijzigingen.'}
                        </div>
                      ) : (
                        <></>
                      )}
                    </div>
                    {!loadingCustomer && !errorCustomer && (
                      <DetailForm
                        schema={customerDetailSchema}
                        title={'Klant'}
                        caption={selectedCustomer?.id ? 'Klant updaten' : 'Klant toevoegen'}
                      />
                    )}
                    {loadingCustomer && (
                      <div className='h-5/6 flex items-center'>
                        <div className={classNames('sweet-loading text-center w-full')}>
                          <BeatLoader color={'#A9C1C9'} loading={true} size={15} aria-label='Loading Spinner' data-testid='loader' />
                        </div>
                      </div>
                    )}
                    {errorCustomer && <p>Error</p>}
                  </>
                ) : (
                  <div />
                )}
              </>
            </Modal>
          </div>
        )}
      </div>
    </div>
  );
};

export default Customers;
